// components/CreateLeague.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCheck,
  faUsers,
  faCalendarAlt,
  faCog,
  faInfoCircle,
  faTrophy,
  faPlus,
  faTimes,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

import { useCreateLeague } from "../../hooks/league/useCreateLeague";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";
import { useFetchUser } from "../../hooks/user/useFetchUser";

import Loading from "../../components/common/Loading";
import DatePicker from "../../components/league/DatePicker";
import PageHeader from "../../components/common/PageHeader";

import useToast from "../../hooks/useToast";

function CreateLeague() {
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [leagueName, setLeagueName] = useState("");
  const [numPlayers, setNumPlayers] = useState("8");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [numberOfEventsInput, setNumberOfEventsInput] = useState("6");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [autoSelectPenalty, setAutoSelectPenalty] = useState(".25");
  const [inviteEmails, setInviteEmails] = useState("");
  const [enableLeagueChat, setEnableLeagueChat] = useState(true);
  const [invitedMembers, setInvitedMembers] = useState([]);

  // Form validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const navigate = useNavigate();
  const toast = useToast();

  const {
    data: dbUser,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useFetchUser();

  const {
    data: upcomingEvents,
    isLoading: isLoadingEvents,
    error: fetchScheduledEventsError,
  } = useFetchScheduledEvents();

  const {
    mutate: createLeague,
    isLoading,
    isError: isLeagueError,
    error,
  } = useCreateLeague(navigate);

  useEffect(() => {
    // Assuming upcomingEvents is an array of events
    const filteredEvents =
      upcomingEvents?.filter(
        (event) => new Date(event.DateTime) >= new Date(startDate),
      ) || [];

    const adjustedNumberOfEvents = Math.min(
      filteredEvents.length,
      parseInt(numberOfEventsInput, 10),
    );

    setSelectedEvents(filteredEvents.slice(0, adjustedNumberOfEvents));

    if (adjustedNumberOfEvents > 0) {
      const lastEvent = filteredEvents[adjustedNumberOfEvents - 1];
      setEndDate(new Date(lastEvent.DateTime));
    }
  }, [startDate, numberOfEventsInput, upcomingEvents]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Validate all fields before submitting
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setCurrentStep(getStepWithErrors(formErrors));
      toast.error("Please correct the errors before submitting");
      return;
    }

    // Show a loading toast
    const loadingToast = toast.loading(
      "Creating your league and sending invitations...",
    );

    // Extract event IDs from selected events
    const eventIds = selectedEvents.map((event) => String(event.EventId));

    createLeague(
      {
        leagueName,
        numPlayers: parseInt(numPlayers, 10),
        startDate,
        endDate,
        userId: dbUser?._id,
        autoSelectPenalty,
        invitedMembers,
        enableLeagueChat,
        eventIds, // NEW: Send array of event IDs
      },
      {
        onSuccess: () => {
          toast.dismiss(loadingToast);
        },
        onError: () => {
          toast.dismiss(loadingToast);
        },
      },
    );
  };

  // Form validation function
  const validateForm = () => {
    const formErrors = {};

    // Step 1 validation
    if (!leagueName.trim()) {
      formErrors.leagueName = "League name is required";
    } else if (leagueName.length < 3) {
      formErrors.leagueName = "League name must be at least 3 characters";
    }

    // Step 2 validation
    if (parseInt(numberOfEventsInput, 10) < 1) {
      formErrors.numberOfEventsInput = "At least one event is required";
    }

    // Step 3 - inviting members is optional

    return formErrors;
  };

  // Find the step that contains errors
  const getStepWithErrors = (formErrors) => {
    if (formErrors.leagueName) return 1;
    if (formErrors.numberOfEventsInput) return 2;
    if (formErrors.invitedMembers) return 3;
    return currentStep;
  };

  // Handle input blur for validation
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });

    // Validate single field
    const fieldErrors = {};
    if (field === "leagueName") {
      if (!leagueName.trim()) {
        fieldErrors.leagueName = "League name is required";
      } else if (leagueName.length < 3) {
        fieldErrors.leagueName = "League name must be at least 3 characters";
      }
    }

    if (field === "numberOfEventsInput") {
      if (parseInt(numberOfEventsInput, 10) < 1) {
        fieldErrors.numberOfEventsInput = "At least one event is required";
      }
    }

    setErrors({ ...errors, ...fieldErrors });
  };

  const handleCancel = () => {
    navigate("/"); // Navigate to the home page
  };

  const goToNextStep = () => {
    // Form validation by step
    const stepErrors = {};

    if (currentStep === 1) {
      if (!leagueName.trim()) {
        stepErrors.leagueName = "League name is required";
      } else if (leagueName.length < 3) {
        stepErrors.leagueName = "League name must be at least 3 characters";
      }
    } else if (currentStep === 2) {
      if (parseInt(numberOfEventsInput, 10) < 1) {
        stepErrors.numberOfEventsInput = "At least one event is required";
      }

      if (selectedEvents.length === 0) {
        stepErrors.selectedEvents = "No events found for the selected date";
      }
    }

    if (Object.keys(stepErrors).length > 0) {
      setErrors({ ...errors, ...stepErrors });
      return;
    }

    // Clear errors for the current step
    const newErrors = { ...errors };
    if (currentStep === 1) {
      delete newErrors.leagueName;
    } else if (currentStep === 2) {
      delete newErrors.numberOfEventsInput;
      delete newErrors.selectedEvents;
    } else if (currentStep === 3) {
      delete newErrors.invitedMembers;
    }
    setErrors(newErrors);

    setCurrentStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const addInviteMember = () => {
    if (!inviteEmails.trim()) return;

    const emails = inviteEmails.split(",").map((email) => email.trim());
    const validEmails = emails.filter(
      (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        !invitedMembers.includes(email),
    );

    if (validEmails.length) {
      setInvitedMembers([...invitedMembers, ...validEmails]);
      setInviteEmails("");
    } else {
      toast.error("Please enter valid email addresses");
    }
  };

  const removeMember = (email) => {
    setInvitedMembers(invitedMembers.filter((member) => member !== email));
  };

  if (isLoading || isUserLoading || isLoadingEvents) return <Loading />;
  if (isLeagueError) return <div>Error creating league: {error.message}</div>;
  if (fetchScheduledEventsError)
    return <div>Error fetching events: {fetchScheduledEventsError.error}</div>;
  if (isUserError) return <div>Error fetching user: {userError}</div>;

  // Progress bar calculation
  const progress = ((currentStep - 1) / 3) * 100;

  const steps = [
    { icon: faInfoCircle, label: "Basics", step: 1 },
    { icon: faCalendarAlt, label: "Events", step: 2 },
    { icon: faUsers, label: "Members", step: 3 },
    { icon: faCheck, label: "Review", step: 4 },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create League"
        subtitle="Set up your fantasy MMA league"
        icon={faTrophy}
        backgroundIcon={faUsers}
      />

      {/* Mobile-First Progress Steps */}
      <div className="bg-white rounded-xl shadow-sm border border-light-grey/30">
        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-medium-grey mb-2">
              <span>Step {currentStep} of 4</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <div className="w-full bg-light-grey/50 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-deep-red to-bright-red h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Step Indicators - Mobile Optimized */}
          <div className="grid grid-cols-4 gap-2">
            {steps.map((step) => (
              <div key={step.step} className="flex flex-col items-center">
                <div
                  className={`
                  w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-200
                  ${
                    currentStep >= step.step
                      ? "bg-deep-red text-white shadow-md"
                      : "bg-light-grey/50 text-medium-grey"
                  }
                `}
                >
                  <FontAwesomeIcon icon={step.icon} className="text-sm" />
                </div>
                <span
                  className={`text-xs font-medium ${
                    currentStep >= step.step
                      ? "text-deep-red"
                      : "text-medium-grey"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-xl shadow-sm border border-light-grey/30">
        <form onSubmit={(e) => e.preventDefault()} className="p-6">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-8">
              {/* Simplified Header */}
              <div className="text-center">
                <h2 className="text-3xl font-bold text-dark-grey mb-2">
                  League Basics
                </h2>
                <p className="text-medium-grey">
                  Create your fantasy MMA league
                </p>
              </div>

              <div className="space-y-8">
                {/* League Name */}
                <div>
                  <label className="block text-dark-grey text-lg font-bold mb-4 flex items-center">
                    <div className="w-8 h-8 bg-deep-red rounded-lg flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faTrophy}
                        className="text-white text-sm"
                      />
                    </div>
                    League Name
                  </label>
                  <input
                    type="text"
                    value={leagueName}
                    onChange={(e) => setLeagueName(e.target.value)}
                    onBlur={() => handleBlur("leagueName")}
                    className={`
                      w-full px-6 py-4 text-lg border-2 rounded-xl transition-all duration-200 font-medium
                      focus:outline-none focus:ring-0 focus:border-deep-red focus:bg-white
                      ${
                        errors.leagueName && touched.leagueName
                          ? "border-bright-red bg-light-grey/50"
                          : "border-light-grey bg-white hover:border-medium-grey"
                      }
                    `}
                    placeholder="Enter your league name"
                  />
                  {errors.leagueName && touched.leagueName && (
                    <p className="text-bright-red text-sm mt-3 flex items-center font-medium">
                      <FontAwesomeIcon icon={faTimes} className="mr-2" />
                      {errors.leagueName}
                    </p>
                  )}
                </div>

                {/* League Size */}
                <div>
                  <label className="block text-dark-grey text-lg font-bold mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-white text-sm"
                      />
                    </div>
                    League Size
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { value: "8", label: "8" },
                      { value: "10", label: "10" },
                      { value: "12", label: "12" },
                      { value: "16", label: "16" },
                      { value: "30", label: "Any" },
                    ].map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        onClick={() => setNumPlayers(size.value)}
                        className={`
                          p-6 rounded-xl transition-all duration-200 text-center border-2
                          ${
                            numPlayers === size.value
                              ? "bg-deep-red text-white border-deep-red shadow-lg transform scale-105"
                              : "bg-white border-light-grey hover:border-deep-red hover:shadow-md text-dark-grey"
                          }
                        `}
                      >
                        <div
                          className={`font-bold text-2xl mb-1 ${
                            numPlayers === size.value
                              ? "text-white"
                              : "text-deep-red"
                          }`}
                        >
                          {size.label}
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            numPlayers === size.value
                              ? "text-white"
                              : "text-medium-grey"
                          }`}
                        >
                          Players
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* First Event Date */}
                <div>
                  <label className="block text-dark-grey text-lg font-bold mb-4 flex items-center">
                    <div className="w-8 h-8 bg-deep-red rounded-lg flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-white text-sm"
                      />
                    </div>
                    First Event Date
                  </label>
                  <div className="bg-light-grey/30 p-6 rounded-xl border border-light-grey">
                    <DatePicker
                      selectedDate={startDate}
                      setSelectedDate={setStartDate}
                      minDate={new Date()}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Event Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-deep-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-2xl text-deep-red"
                  />
                </div>
                <h2 className="text-2xl font-bold text-dark-grey mb-2">
                  Event Selection
                </h2>
                <p className="text-medium-grey">
                  Configure your league events and rules
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-dark-grey text-sm font-semibold mb-3">
                    Number of Events <span className="text-deep-red">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {["4", "6", "8", "10", "12", "16"].map((eventCount) => (
                      <button
                        key={eventCount}
                        type="button"
                        onClick={() => setNumberOfEventsInput(eventCount)}
                        className={`
                          p-4 rounded-xl border-2 transition-all duration-200 text-center
                          ${
                            numberOfEventsInput === eventCount
                              ? "border-deep-red bg-deep-red/5 text-deep-red"
                              : "border-light-grey bg-white hover:border-medium-grey text-dark-grey"
                          }
                        `}
                      >
                        <div
                          className={`font-bold text-lg mb-1 ${
                            numberOfEventsInput === eventCount
                              ? "text-deep-red"
                              : "text-dark-grey"
                          }`}
                        >
                          {eventCount}
                        </div>
                        <div className="text-xs text-medium-grey">Events</div>
                      </button>
                    ))}
                  </div>
                  {errors.numberOfEventsInput &&
                    touched.numberOfEventsInput && (
                      <p className="text-bright-red text-sm mt-2 flex items-center">
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        {errors.numberOfEventsInput}
                      </p>
                    )}
                  <p className="mt-2 text-sm text-medium-grey">
                    Your league will include this many events starting from your
                    first event date
                  </p>
                </div>

                <div>
                  <label className="block text-dark-grey text-sm font-semibold mb-3">
                    Auto-Select Penalty
                  </label>
                  <div className="space-y-3">
                    {[
                      { value: "0", label: "No penalty", desc: "100% points" },
                      {
                        value: ".25",
                        label: "25% penalty",
                        desc: "75% points",
                      },
                      {
                        value: ".50",
                        label: "50% penalty",
                        desc: "50% points",
                      },
                      {
                        value: ".75",
                        label: "75% penalty",
                        desc: "25% points",
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setAutoSelectPenalty(option.value)}
                        className={`
                          w-full p-4 rounded-xl border-2 transition-all duration-200 text-left
                          ${
                            autoSelectPenalty === option.value
                              ? "border-deep-red bg-deep-red/5"
                              : "border-light-grey bg-white hover:border-medium-grey"
                          }
                        `}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div
                              className={`font-semibold ${
                                autoSelectPenalty === option.value
                                  ? "text-deep-red"
                                  : "text-dark-grey"
                              }`}
                            >
                              {option.label}
                            </div>
                            <div className="text-sm text-medium-grey">
                              {option.desc}
                            </div>
                          </div>
                          {autoSelectPenalty === option.value && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="text-deep-red"
                            />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-medium-grey">
                    Points deducted when a member doesn't manually select
                    fighters
                  </p>
                </div>

                <div>
                  <label className="block text-dark-grey text-sm font-semibold mb-3">
                    Selected Events <span className="text-deep-red">*</span>
                  </label>

                  {errors.selectedEvents && (
                    <div className="mb-4 p-3 bg-light-grey/50 border border-bright-red rounded-lg">
                      <p className="text-bright-red text-sm flex items-center">
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        {errors.selectedEvents}
                      </p>
                    </div>
                  )}

                  <div className="bg-light-grey/20 rounded-xl border border-light-grey overflow-hidden">
                    {selectedEvents.length > 0 ? (
                      <div className="divide-y divide-light-grey">
                        {selectedEvents.map((event, index) => (
                          <div
                            key={index}
                            className="p-4 hover:bg-white/50 transition-colors"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0 w-12 h-12 bg-deep-red rounded-lg flex flex-col items-center justify-center text-white">
                                <span className="text-xs font-bold">
                                  {new Date(event.DateTime).toLocaleString(
                                    "default",
                                    { month: "short" },
                                  )}
                                </span>
                                <span className="text-sm font-bold">
                                  {new Date(event.DateTime).getDate()}
                                </span>
                              </div>

                              <div className="flex-grow min-w-0">
                                <h4 className="font-semibold text-dark-grey truncate">
                                  {event.Name}
                                </h4>
                                <p className="text-sm text-medium-grey">
                                  {new Date(
                                    event.DateTime,
                                  ).toLocaleDateString()}{" "}
                                  at{" "}
                                  {new Date(event.DateTime).toLocaleTimeString(
                                    [],
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </p>
                              </div>

                              <div className="flex-shrink-0">
                                <span
                                  className={`
                                  inline-block px-3 py-1 rounded-full text-xs font-semibold
                                  ${
                                    index === 0
                                      ? "bg-deep-red/10 text-deep-red"
                                      : index === selectedEvents.length - 1
                                        ? "bg-gold/10 text-gold"
                                        : "bg-light-grey text-medium-grey"
                                  }
                                `}
                                >
                                  {index === 0
                                    ? "First"
                                    : index === selectedEvents.length - 1
                                      ? "Last"
                                      : `#${index + 1}`}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-light-grey/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className="text-2xl text-medium-grey"
                          />
                        </div>
                        <h3 className="font-semibold text-dark-grey mb-2">
                          No events found
                        </h3>
                        <p className="text-medium-grey text-sm">
                          Try adjusting your first event date or selecting more
                          events
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Member Management */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-deep-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="text-2xl text-deep-red"
                  />
                </div>
                <h2 className="text-2xl font-bold text-dark-grey mb-2">
                  Invite Members
                </h2>
                <p className="text-medium-grey">
                  Optionally add friends to join your league
                </p>
              </div>

              <div className="space-y-6">
                {/* Email Input */}
                <div>
                  <label className="block text-dark-grey text-lg font-bold mb-4 flex items-center">
                    <div className="w-8 h-8 bg-deep-red rounded-lg flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-white text-sm"
                      />
                    </div>
                    Add Members
                  </label>

                  <div className="space-y-4">
                    <div className="flex space-x-3">
                      <input
                        type="email"
                        value={inviteEmails}
                        onChange={(e) => setInviteEmails(e.target.value)}
                        placeholder="Enter email address"
                        className={`
                          flex-1 px-4 py-3 text-base border-2 rounded-xl transition-all duration-200
                          focus:outline-none focus:ring-0 focus:border-deep-red
                          ${
                            errors.invitedMembers
                              ? "border-bright-red bg-white"
                              : "border-light-grey bg-white hover:border-medium-grey"
                          }
                        `}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addInviteMember();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addInviteMember}
                        disabled={!inviteEmails.trim()}
                        className={`
                          px-6 py-3 rounded-xl font-semibold transition-colors flex items-center
                          ${
                            inviteEmails.trim()
                              ? "bg-deep-red text-white hover:bg-bright-red"
                              : "bg-light-grey text-medium-grey cursor-not-allowed"
                          }
                        `}
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add
                      </button>
                    </div>

                    {errors.invitedMembers && (
                      <div className="p-3 bg-white border border-bright-red rounded-lg">
                        <p className="text-bright-red text-sm flex items-center font-medium">
                          <FontAwesomeIcon icon={faTimes} className="mr-2" />
                          {errors.invitedMembers}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Members List */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-dark-grey text-lg font-bold flex items-center">
                      <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center mr-3">
                        <FontAwesomeIcon
                          icon={faUsers}
                          className="text-white text-sm"
                        />
                      </div>
                      League Members
                    </label>
                    <span
                      className={`text-sm font-semibold px-3 py-1 rounded-full ${
                        invitedMembers.length > parseInt(numPlayers)
                          ? "bg-bright-red/10 text-bright-red"
                          : invitedMembers.length === parseInt(numPlayers)
                            ? "bg-gold/10 text-gold"
                            : "bg-deep-red/10 text-deep-red"
                      }`}
                    >
                      {invitedMembers.length}/{numPlayers}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl border border-light-grey min-h-[200px] overflow-hidden">
                    {invitedMembers.length > 0 ? (
                      <div className="divide-y divide-light-grey">
                        {invitedMembers.map((email, index) => (
                          <div
                            key={index}
                            className="p-4 hover:bg-light-grey/20 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-grow min-w-0">
                                <div className="relative">
                                  <div className="w-10 h-10 bg-deep-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FontAwesomeIcon
                                      icon={faEnvelope}
                                      className="text-deep-red"
                                    />
                                  </div>
                                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-deep-red rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">
                                      {index + 1}
                                    </span>
                                  </div>
                                </div>
                                <div className="min-w-0 flex-grow">
                                  <p className="font-semibold text-dark-grey truncate">
                                    {email}
                                  </p>
                                  <p className="text-sm text-medium-grey">
                                    Invitation pending
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeMember(email)}
                                className="ml-3 p-2 text-medium-grey hover:text-bright-red hover:bg-bright-red/10 rounded-lg transition-colors flex-shrink-0"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-light-grey/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-2xl text-medium-grey"
                          />
                        </div>
                        <h3 className="font-semibold text-dark-grey mb-2">
                          No members invited yet
                        </h3>
                        <p className="text-medium-grey text-sm">
                          Add email addresses to invite friends to your league
                        </p>
                      </div>
                    )}
                  </div>

                  {invitedMembers.length > parseInt(numPlayers) && (
                    <div className="mt-3 p-4 bg-white border border-bright-red rounded-lg">
                      <p className="text-bright-red text-sm flex items-center font-medium">
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        You've invited {invitedMembers.length} people but your
                        league only has {numPlayers} spots. Remove some
                        invitations or increase your league size.
                      </p>
                    </div>
                  )}
                </div>

                {/* League Chat Setting */}
                <div>
                  <label className="block text-dark-grey text-lg font-bold mb-4 flex items-center">
                    <div className="w-8 h-8 bg-deep-red rounded-lg flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faCog}
                        className="text-white text-sm"
                      />
                    </div>
                    League Settings
                  </label>

                  <div className="bg-white p-4 rounded-xl border border-light-grey">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-deep-red/10 rounded-lg flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-deep-red"
                          />
                        </div>
                        <div>
                          <p className="font-semibold text-dark-grey">
                            League Chat
                          </p>
                          <p className="text-sm text-medium-grey">
                            Allow members to chat within your league
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEnableLeagueChat(!enableLeagueChat)}
                        className={`
                          relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0
                          ${enableLeagueChat ? "bg-deep-red" : "bg-light-grey"}
                        `}
                      >
                        <span
                          className={`
                          inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm
                          ${
                            enableLeagueChat ? "translate-x-6" : "translate-x-1"
                          }
                        `}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-deep-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-2xl text-deep-red"
                  />
                </div>
                <h2 className="text-2xl font-bold text-dark-grey mb-2">
                  Review & Create
                </h2>
                <p className="text-medium-grey">
                  Double-check your league details
                </p>
              </div>

              <div className="bg-deep-red/5 p-6 rounded-xl border border-deep-red/20">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-deep-red rounded-xl flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faTrophy}
                      className="text-white text-lg"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-dark-grey">
                      {leagueName}
                    </h3>
                    <p className="text-medium-grey">
                      Ready to launch your league
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
                  <div className="bg-light-grey/30 px-4 py-3 border-b border-light-grey">
                    <h4 className="font-semibold text-dark-grey">
                      League Settings
                    </h4>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-medium-grey">League Size</span>
                      <span className="font-semibold text-dark-grey">
                        {numPlayers} players
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-medium-grey">First Event</span>
                      <span className="font-semibold text-dark-grey">
                        {startDate.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-medium-grey">Last Event</span>
                      <span className="font-semibold text-dark-grey">
                        {endDate.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-medium-grey">
                        Auto-Select Penalty
                      </span>
                      <span className="font-semibold text-dark-grey">
                        {autoSelectPenalty === "0"
                          ? "None"
                          : `${parseFloat(autoSelectPenalty) * 100}%`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-medium-grey">League Chat</span>
                      <span
                        className={`font-semibold px-2 py-1 rounded-full text-xs ${
                          enableLeagueChat
                            ? "bg-deep-red/10 text-deep-red"
                            : "bg-light-grey text-medium-grey"
                        }`}
                      >
                        {enableLeagueChat ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
                  <div className="bg-light-grey/30 px-4 py-3 border-b border-light-grey">
                    <h4 className="font-semibold text-dark-grey">
                      Members ({invitedMembers.length}/{numPlayers})
                    </h4>
                  </div>
                  <div className="p-4">
                    {invitedMembers.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {invitedMembers.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-3 p-2 bg-light-grey/20 rounded-lg"
                          >
                            <div className="w-6 h-6 bg-deep-red rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                            <span className="text-sm text-dark-grey truncate">
                              {email}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-medium-grey text-sm py-4">
                        No members invited
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
                  <div className="bg-light-grey/30 px-4 py-3 border-b border-light-grey">
                    <h4 className="font-semibold text-dark-grey">
                      Events ({selectedEvents.length})
                    </h4>
                  </div>
                  <div className="p-4">
                    {selectedEvents.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedEvents.map((event, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-3 p-2 bg-light-grey/20 rounded-lg"
                          >
                            <div className="w-8 h-8 bg-deep-red rounded-lg flex flex-col items-center justify-center text-white text-xs">
                              <span className="font-bold leading-none">
                                {new Date(event.DateTime).getDate()}
                              </span>
                            </div>
                            <div className="flex-grow min-w-0">
                              <p className="text-sm font-medium text-dark-grey truncate">
                                {event.Name}
                              </p>
                              <p className="text-xs text-medium-grey">
                                {new Date(event.DateTime).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-medium-grey text-sm py-4">
                        No events selected
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons - Mobile Optimized */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-light-grey/50">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={goToPreviousStep}
                className="flex items-center px-6 py-3 text-medium-grey hover:text-dark-grey transition-colors"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                Previous
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center px-6 py-3 text-medium-grey hover:text-dark-grey transition-colors"
              >
                Cancel
              </button>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={goToNextStep}
                className="flex items-center px-6 py-3 bg-deep-red text-white rounded-xl font-semibold hover:bg-bright-red transition-colors shadow-sm"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex items-center px-6 py-3 bg-deep-red text-white rounded-xl font-semibold hover:bg-bright-red transition-colors shadow-sm disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    Create League
                    <FontAwesomeIcon icon={faCheck} className="ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateLeague;
